// @ts-strict-ignore
import debounce from 'lodash.debounce';
import React, {
  FocusEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Autosuggest, {
  ChangeEvent,
  InputProps,
  RenderSuggestionsContainerParams,
} from 'react-autosuggest';
import { useDispatch } from 'react-redux';

import { useGMapsErrored } from 'cms/hooks';
import { setGMapsError } from 'store/main/ducks/gMapsError';
import { REMOTE_KEYWORDS } from 'utils/constants/search';
import { loadGoogleMaps } from 'utils/loadGoogleMaps';

import { AutosuggestWrapper } from './AutosuggestWrapper';
import { LocationInputSuggestion } from './LocationInputSuggestion';
import { StyledInput } from './StyledInput';
import { SuggestionsContainer } from './SuggestionsContainer';
import { getCity } from './getCity';
import { getPlacePredictions } from './getPlacePredictions';
import { getRemoteOptions } from './getRemoteOptions';

const defaultKeyword = REMOTE_KEYWORDS[CURRENT_LOCALE][0];

type Props = {
  onLocationChange: (
    arg0: SearchLocation,
    arg1: boolean | null | undefined,
    arg2: boolean | null | undefined
  ) => void;
  onLocationInputValueChange?: (
    event: React.FormEvent<unknown>,
    params: ChangeEvent
  ) => void;
  hasSuccess?: boolean;
  clearLocation: () => void;
  locationInputValue: string;
  'data-qa-id'?: string;
  placeholder?: string | null;
  showLocationIcon?: boolean;
  showClearButton: boolean;
  className?: string;
  handlePlaceDetails?: (arg0: Place) => void;
  addressTypes?: Array<string>;
  includeCity?: boolean;
  includeFull?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  hasRemoteOptions?: boolean;
  highlightFirstSuggestion?: boolean;
  componentRestrictions?: google.maps.places.ComponentRestrictions;
};

export function LocationInput({
  hasSuccess,
  onLocationChange,
  onLocationInputValueChange,
  clearLocation,
  locationInputValue,
  placeholder,
  showLocationIcon = false,
  showClearButton,
  className,
  addressTypes,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion = true,
  componentRestrictions,
  hasRemoteOptions,
  onBlur,
  handlePlaceDetails: handlePlaceDetailsProp,
  'data-qa-id': dataQaId,
}: Props) {
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService>();
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken>();
  const dispatch = useDispatch();

  const gMapsErrored = useGMapsErrored();

  const handlePlaceDetails = useCallback(
    (place: google.maps.GeocoderResult) => {
      if (handlePlaceDetailsProp) {
        handlePlaceDetailsProp(place);
        return;
      }

      const addressComponents: Record<string, boolean | string> =
        place.address_components.reduce((hash, component) => {
          const attributeName = component.types[0];

          return {
            ...hash,
            [attributeName]: [
              'country',
              'administrative_area_level_1',
            ].includes(attributeName)
              ? component.short_name
              : component.long_name,
          };
        }, {});

      if (!('country' in addressComponents)) {
        // below: special handling of 'south east asia', 'gaza', etc.
        // i.e. results for which the autocomplete api does not return a country code
        // we will use the iso 3166 user-defined 'XA' code for this situation
        // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#User-assigned_code_elements
        addressComponents.country = 'XA';
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-param-reassign
        place.types[0] = 'undefined_country_geocodes';
      }

      const countryCode = addressComponents.country;
      const stateCode = addressComponents.administrative_area_level_1;
      let city;
      let full;

      if (includeCity) {
        city = getCity(addressComponents);
      }

      if (includeFull) {
        full = place.formatted_address;
      }

      const boundingBox = place.geometry.viewport.toJSON();
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 1.
      onLocationChange({
        text: place.formatted_address,
        stateCode,
        countryCode,
        boundingBoxN: boundingBox.north,
        boundingBoxW: boundingBox.west,
        boundingBoxS: boundingBox.south,
        boundingBoxE: boundingBox.east,
        geoType: place.types[0],
        latLongAddress: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        full,
        city,
      });
    },
    [handlePlaceDetailsProp, includeCity, includeFull, onLocationChange]
  );

  const onSuggestionSelected = useCallback(
    async (e, { suggestion }) => {
      if (!geocoder) return;

      if (suggestion.isRemoteOption) {
        onLocationChange(
          {
            text: null,
            stateCode: null,
            countryCode: null,
            boundingBoxN: null,
            boundingBoxW: null,
            boundingBoxS: null,
            boundingBoxE: null,
            geoType: null,
            latitude: null,
            longitude: null,
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ text: null; stateCode: null; c... Remove this comment to see the full error message
            full: null,
            city: null,
          },
          true,
          true
        );

        return;
      }

      await geocoder.geocode(
        {
          placeId: suggestion.place_id,
        },
        (results) => handlePlaceDetails(results[0])
      );
    },
    [geocoder, handlePlaceDetails, onLocationChange]
  );

  const [suggestions, setSuggestions] = useState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ReadonlyArray<Record<string, any>>
  >([]);

  const [inProgressQuery, setInProgressQuery] = useState<
    | Promise<
        ReadonlyArray<{
          name: string;
          value: string;
        }>
      >
    | null
    | undefined
  >(null);

  useEffect(() => {
    if (!autocompleteService) {
      loadGoogleMaps()
        .then((maps) => {
          setAutocompleteService(new maps.places.AutocompleteService());
          setGeocoder(new maps.Geocoder());
          setSessionToken(new maps.places.AutocompleteSessionToken());
        })
        .catch((error) => {
          dispatch(setGMapsError(error.message));
        });
    }
  }, [autocompleteService, dispatch]);

  const loadSuggestions = useCallback(
    (value: string) => {
      // Cancel the previous request
      if (inProgressQuery) {
        inProgressQuery.then(() => {
          debounce(() => loadSuggestions(value));
        });
        return undefined;
      }

      const query = getPlacePredictions({
        autocompleteService,
        input: value,
        componentRestrictions,
        addressTypes,
        sessionToken,
      });
      const remoteOptions = [];

      if (hasRemoteOptions) {
        remoteOptions.push(getRemoteOptions({ value, defaultKeyword }));
      }

      query.then((options) => {
        // @ts-expect-error fix me
        setSuggestions([...remoteOptions, ...options].filter(Boolean));
        setInProgressQuery(null);
      });

      // @ts-expect-error Fix me
      setInProgressQuery(query);
      return query;
    },
    [
      addressTypes,
      autocompleteService,
      componentRestrictions,
      hasRemoteOptions,
      inProgressQuery,
      sessionToken,
    ]
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: string }) => {
      if (autocompleteService) {
        loadSuggestions(value);
      }
    },
    [autocompleteService, loadSuggestions]
  );

  const renderSuggestion = useCallback(
    (suggestion) => <LocationInputSuggestion suggestion={suggestion} />,
    []
  );

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
    setInProgressQuery(null);
  }, []);

  const renderSuggestionsContainer = useCallback(
    ({ containerProps, children }: RenderSuggestionsContainerParams) => (
      <SuggestionsContainer containerProps={containerProps}>
        {children}
      </SuggestionsContainer>
    ),
    []
  );

  const renderInputComponent = useCallback(
    (renderInputComponentProps: InputProps<unknown>) => (
      <StyledInput
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...renderInputComponentProps}
        hasSuccess={hasSuccess}
        onClear={showClearButton ? clearLocation : null}
        gMapsErrored={gMapsErrored}
        showLocationIcon={showLocationIcon}
        data-qa-id={dataQaId || 'location-input'}
      />
    ),
    [
      clearLocation,
      gMapsErrored,
      hasSuccess,
      dataQaId,
      showClearButton,
      showLocationIcon,
    ]
  );

  const onKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const getSuggestionValue = useCallback(
    (suggestion) => suggestion.description,
    []
  );

  return (
    <AutosuggestWrapper borderSize={4}>
      <Autosuggest
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={(v) => v && v.toString().trim().length >= 2}
        renderInputComponent={renderInputComponent}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          className,
          disabled,
          onChange: onLocationInputValueChange,
          onBlur,
          onKeyPress,
          placeholder,
          value: locationInputValue || '',
          'aria-label': getText('Location'),
        }}
      />
    </AutosuggestWrapper>
  );
}
